import { useForm } from '@mantine/form';
import {
  Stack,
  Box,
  Group,
  Text,
  Button,
  SimpleGrid,
  TextInput,
  NumberInput,
  Select,
  Checkbox,
  Radio,
  Grid,
  Alert,
  MantineProvider,
  Flex,
  useMantineTheme,
  Divider,
} from '@mantine/core';
import {
  IconArrowRight,
  IconUpload,
  IconX,
  IconCloudUpload,
  IconInfoCircle,
  IconCalendar,
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { Dropzone, DropzoneProps, PDF_MIME_TYPE } from '@mantine/dropzone';

import type {
  ProfileType,
  DisbursementFeeProcessingMethod,
} from '../../models/contract.ts';
import type { Attachment, NewAttachment } from '../../models/attachment.ts';
import type {
  RefundFeeProcessingMethod,
  BillingCycle,
  ParkingLotFunctionType,
  ProfitSharingConfig,
} from '../../models/parking-contract.ts';

import {
  PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  PROFILE_TYPE_LABEL,
  PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL,
  PARKING_CONTRACT_BILLING_CYCLE_LABEL,
  PARKING_CONTRACT_DEFAULT_PROFIT_SHARING_TYPE_LABEL,
  PARKING_CONTRACT_BANK_DISCOUNT_PROFIT_SHARING_TYPE_LABEL,
  PARKING_CONTRACT_PBA_REFERRAL_PROFIT_SHARING_TYPE_LABEL,
  PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL,
  PARKING_CONTRACT_PROFIT_SHARING_UNIT,
  labelMapToOptionList,
} from '../../const/labels.ts';
import { notifySuccess } from '../../utils/NotifySuccess.tsx';
import { notifyError } from '../../utils/NotifyError.tsx';

export interface MutateParkingContractFormValues {
  name: string;
  profileType: ProfileType;
  paperContractStartAt: string;
  paperContractEndAt: string;
  activeStartAt: string;
  activeEndAt: string;
  disbursementFeeProcessingMethod: DisbursementFeeProcessingMethod;
  refundFeeProcessingMethod: RefundFeeProcessingMethod;
  billingCycle: BillingCycle;
  parkingLotFunctionTypes: ParkingLotFunctionType[];
  profitSharingConfigs: {
    ALPR: ProfitSharingConfig;
    KIOSK: ProfitSharingConfig;
    PAY_TAIPEI: ProfitSharingConfig;
  };
  existingAttachments: Attachment[];
  newAttachments: NewAttachment[];
  deletedAttachmentIds: string[];
}

export type MutateParkingContractFormInitialValues = Omit<
  MutateParkingContractFormValues,
  'newAttachments' | 'deletedAttachmentIds'
>;

interface MutateParkingContractFormProps {
  initialValues: MutateParkingContractFormInitialValues;
  onSubmit: (formValues: MutateParkingContractFormValues) => void;
}

const dropZoneProps: Partial<DropzoneProps> = {
  accept: PDF_MIME_TYPE,
  maxSize: 30000000, // 30MB
};
const profileTypeSelections = labelMapToOptionList(PROFILE_TYPE_LABEL);
const disbursementFeeProcessingMethodSelections = labelMapToOptionList(
  PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
);
const refundFeeProcessingMethodSelections = labelMapToOptionList(
  PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL,
);
const billingCycleSelections = labelMapToOptionList(
  PARKING_CONTRACT_BILLING_CYCLE_LABEL,
);
const defaultProfitSharingTypeSelections = labelMapToOptionList(
  PARKING_CONTRACT_DEFAULT_PROFIT_SHARING_TYPE_LABEL,
).map(({ label, value }) => ({
  label: `${label} （${PARKING_CONTRACT_PROFIT_SHARING_UNIT[value]}）`,
  value,
}));
const bankDiscountProfitSharingTypeSelections = labelMapToOptionList(
  PARKING_CONTRACT_BANK_DISCOUNT_PROFIT_SHARING_TYPE_LABEL,
).map(({ label, value }) => ({
  label:
    value === 'NON'
      ? label
      : `${label} （${PARKING_CONTRACT_PROFIT_SHARING_UNIT[value]}）`,
  value,
}));
const pbaReferralProfitSharingTypeSelections = labelMapToOptionList(
  PARKING_CONTRACT_PBA_REFERRAL_PROFIT_SHARING_TYPE_LABEL,
).map(({ label, value }) => ({
  label:
    value === 'NON'
      ? label
      : `${label} （${PARKING_CONTRACT_PROFIT_SHARING_UNIT[value]}）`,
  value,
}));

export default function MutateParkingContractForm({
  initialValues,
  onSubmit,
}: MutateParkingContractFormProps) {
  // hooks here
  const theme = useMantineTheme();
  const form = useForm<MutateParkingContractFormValues>({
    mode: 'controlled',
    initialValues: {
      newAttachments: [],
      deletedAttachmentIds: [],
      ...initialValues,
    },
    validate: {
      name: (value) => (value ? null : ' '),
      paperContractStartAt: (value, values) =>
        value ? checkStartEndRange(value, values.paperContractEndAt) : ' ',
      paperContractEndAt: (value, values) =>
        value ? checkStartEndRange(values.paperContractStartAt, value) : ' ',
      activeStartAt: (value, values) => {
        if (values.profileType === 'DEFAULT') {
          return value ? checkStartEndRange(value, values.activeEndAt) : ' ';
        }
      },
      activeEndAt: (value, values) => {
        if (values.profileType === 'DEFAULT') {
          return value ? checkStartEndRange(values.activeStartAt, value) : ' ';
        }
      },
      newAttachments: (value, values) => {
        return value.length > 1
          ? '只能上傳一份檔案'
          : value.length +
                values.existingAttachments.length -
                values.deletedAttachmentIds.length <
              1
            ? '附件不可為空'
            : null;
      },
      parkingLotFunctionTypes: (value) => (value.length === 0 ? ' ' : null),
      profitSharingConfigs: {
        ALPR: {
          defaultConfig: {
            value: (value, values) =>
              checkProfitSharingValue(
                value,
                values.parkingLotFunctionTypes,
                'ALPR',
              ),
          },
          bankDiscountConfig: {
            value: (value, values) =>
              values.profitSharingConfigs.ALPR.bankDiscountConfig.type === 'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'ALPR',
                  ),
          },
          pbaReferralConfig: {
            value: (value, values) =>
              values.profitSharingConfigs.ALPR.pbaReferralConfig.type === 'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'ALPR',
                  ),
          },
        },
        KIOSK: {
          defaultConfig: {
            value: (value, values) =>
              checkProfitSharingValue(
                value,
                values.parkingLotFunctionTypes,
                'KIOSK',
              ),
          },
          bankDiscountConfig: {
            value: (value, values) =>
              values.profitSharingConfigs.KIOSK.bankDiscountConfig.type ===
              'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'KIOSK',
                  ),
          },
          pbaReferralConfig: {
            value: (value, values) => {
              return values.profitSharingConfigs.KIOSK.pbaReferralConfig
                .type === 'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'KIOSK',
                  );
            },
          },
        },
        PAY_TAIPEI: {
          defaultConfig: {
            value: (value, values) =>
              checkProfitSharingValue(
                value,
                values.parkingLotFunctionTypes,
                'PAY_TAIPEI',
              ),
          },
          bankDiscountConfig: {
            value: (value, values) =>
              values.profitSharingConfigs.PAY_TAIPEI.bankDiscountConfig.type ===
              'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'PAY_TAIPEI',
                  ),
          },
          pbaReferralConfig: {
            value: (value, values) =>
              values.profitSharingConfigs.PAY_TAIPEI.pbaReferralConfig.type ===
              'NON'
                ? null
                : checkProfitSharingValue(
                    value,
                    values.parkingLotFunctionTypes,
                    'PAY_TAIPEI',
                  ),
          },
        },
      },
    },
  });

  // consts
  const checkBoxStyle = form.errors.parkingLotFunctionTypes
    ? {
        input: { borderColor: 'var(--mantine-color-red-6)' },
        label: { color: 'var(--mantine-color-red-6)' },
      }
    : undefined;

  // functions
  function checkStartEndRange(startAt, endAt) {
    if (!startAt || !endAt) return ' ';
    if (new Date(startAt) > new Date(endAt)) return ' ';
  }
  function checkProfitSharingValue(value, parkingLotFunctionTypes, targetType) {
    if (!parkingLotFunctionTypes.includes(targetType)) return null;
    if (!value) return ' ';
    if (parseFloat(value) <= 0) return ' ';
  }

  const displayAttachments: (Attachment | NewAttachment)[] = [
    ...form.values.existingAttachments.filter(
      (attachment) => !form.values.deletedAttachmentIds.includes(attachment.id),
    ),
    ...form.values.newAttachments,
  ];

  // callback function
  function duplicateAlprProfitSharingConfig() {
    form.setValues((prev) => ({
      ...prev,
      profitSharingConfigs: {
        ...prev.profitSharingConfigs,
        KIOSK: {
          parkingLotFunctionType: 'KIOSK',
          ...prev.profitSharingConfigs.ALPR,
        },
      },
    }));
    notifySuccess('成功複製分潤設定');
  }

  function onDrop(files: File[]): void {
    const attachments = files.map((file) => {
      return {
        displayName: file.name,
        file: file,
      };
    });
    form.setValues((prev) => ({
      ...prev,
      newAttachments: [...prev.newAttachments, ...attachments],
    }));
  }
  function onClickDeleteFile(
    deleteAttachment: Attachment | NewAttachment,
  ): void {
    const isExistingAttachment = 'id' in deleteAttachment;

    // 如果是刪除已存在的檔案，就放進待刪除佇列；反之，從暫存區移除掉即可
    if (isExistingAttachment) {
      form.setValues((prev) => ({
        ...prev,
        deletedAttachmentIds: [
          ...prev.deletedAttachmentIds,
          deleteAttachment.id,
        ],
      }));
    } else {
      form.setValues((prev) => ({
        ...prev,
        newAttachments: prev.newAttachments.filter(
          (attachment) => attachment !== deleteAttachment,
        ),
      }));
    }
  }

  return (
    <form
      id="mutateParkingContractForm"
      onSubmit={form.onSubmit(
        (values: MutateParkingContractFormValues) => onSubmit(values),
        () => {
          notifyError('合約儲存失敗', '尚有欄位未填寫完成或格式錯誤');
        },
      )}
    >
      <Text size="sm" fw={500} c="gray" mt={40}>
        基本設定
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <TextInput
          withAsterisk
          label="合約名稱"
          placeholder="請輸入合約名稱"
          key={form.key('name')}
          {...form.getInputProps('name')}
        />
        <Select
          {...form.getInputProps('profileType')}
          allowDeselect={false}
          label="合約套用方式"
          data={profileTypeSelections}
          defaultValue="DEFAULT"
          onChange={(value: ProfileType) => {
            form.setFieldValue('profileType', value);
            if (value !== 'DEFAULT') {
              form.setValues((prev) => ({
                ...prev,
                activeStartAt: null,
                activeEndAt: null,
              }));
            }
          }}
          key={form.key('profileType')}
        />
        <Group>
          <DatePickerInput
            withAsterisk
            label="紙本合約生效起迄日"
            placeholder="請輸入紙本合約生效起始日"
            rightSection={<IconCalendar size={14} />}
            rightSectionPointerEvents="none"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('paperContractStartAt')}
            {...form.getInputProps('paperContractStartAt')}
          />
          <Stack>
            <Box h={12} /> {/* 塞個東東讓下面的箭頭位置正確 */}
            <IconArrowRight />
          </Stack>
          <DatePickerInput
            label=" "
            placeholder="請輸入紙本合約生效結束日"
            rightSection={<IconCalendar size={14} />}
            rightSectionPointerEvents="none"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('paperContractEndAt')}
            {...form.getInputProps('paperContractEndAt')}
          />
        </Group>
        <Group>
          <DatePickerInput
            withAsterisk
            label="合約生效起迄日"
            placeholder="請輸入合約生效起始日"
            rightSection={<IconCalendar size={14} />}
            rightSectionPointerEvents="none"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            disabled={form.values.profileType !== 'DEFAULT'}
            key={form.key('activeStartAt')}
            {...form.getInputProps('activeStartAt')}
          />
          <Stack>
            <Box h={12} /> {/* 塞個東東讓下面的箭頭位置正確 */}
            <IconArrowRight />
          </Stack>
          <DatePickerInput
            label=" "
            placeholder="請輸入合約生效結束日"
            rightSection={<IconCalendar size={14} />}
            rightSectionPointerEvents="none"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            disabled={form.values.profileType !== 'DEFAULT'}
            key={form.key('activeEndAt')}
            {...form.getInputProps('activeEndAt')}
          />
        </Group>

        <Select
          allowDeselect={false}
          label="手續費扣除方式"
          data={disbursementFeeProcessingMethodSelections}
          key={form.key('disbursementFeeProcessingMethod')}
          {...form.getInputProps('disbursementFeeProcessingMethod')}
        />
        <Select
          allowDeselect={false}
          label="退款手續費處理方式"
          data={refundFeeProcessingMethodSelections}
          key={form.key('refundFeeProcessingMethod')}
          {...form.getInputProps('refundFeeProcessingMethod')}
        />
        <Select
          allowDeselect={false}
          label="出帳週期"
          data={billingCycleSelections}
          key={form.key('billingCycle')}
          {...form.getInputProps('billingCycle')}
        />
        <Checkbox.Group
          mt="md"
          withAsterisk
          label="交易情境"
          {...form.getInputProps('parkingLotFunctionTypes')}
        >
          <Group>
            <MantineProvider theme={{ cursorType: 'pointer' }}>
              <Checkbox value="ALPR" label="車辨" styles={checkBoxStyle} />
              <Checkbox
                value="KIOSK"
                label="繳費機線上化"
                styles={checkBoxStyle}
              />
              <Checkbox
                value="PAY_TAIPEI"
                label="北市路外"
                styles={checkBoxStyle}
              />
            </MantineProvider>
          </Group>
        </Checkbox.Group>
      </SimpleGrid>

      {['ALPR', 'KIOSK', 'PAY_TAIPEI'].map(
        (parkingLotFunctionType: ParkingLotFunctionType) => {
          if (
            !form.values.parkingLotFunctionTypes.includes(
              parkingLotFunctionType,
            )
          ) {
            return null;
          }

          return (
            <>
              <Text size="sm" fw={500} c="gray" mt={40}>
                {`${PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL[parkingLotFunctionType]}分潤設定`}
              </Text>
              <Divider mt={10} mb={20} />

              {form.values.parkingLotFunctionTypes.includes('ALPR') &&
                parkingLotFunctionType === 'KIOSK' && (
                  <>
                    <Alert
                      variant="light"
                      color="gray"
                      mb={20}
                      icon={<IconInfoCircle />}
                      title={`套用車辨分潤設定`}
                    >
                      <Flex justify="space-between" align="center">
                        <Text>
                          如車辨分潤設定與繳費機線上化設定相同，可點選右側的按鈕套用設定
                        </Text>
                        <Button
                          variant="outline"
                          onClick={duplicateAlprProfitSharingConfig}
                        >
                          套用車辨分潤設定
                        </Button>
                      </Flex>
                    </Alert>
                  </>
                )}

              <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                <Grid columns={4}>
                  <Grid.Col span={3}>
                    <Select
                      withAsterisk
                      allowDeselect={false}
                      label="預設分潤條件"
                      data={defaultProfitSharingTypeSelections}
                      style={{ flex: 1 }}
                      key={form.key(
                        `profitSharingConfigs.${parkingLotFunctionType}.defaultConfig.type`,
                      )}
                      {...form.getInputProps(
                        `profitSharingConfigs.${parkingLotFunctionType}.defaultConfig.type`,
                      )}
                    />
                  </Grid.Col>
                  <Grid.Col span={1}>
                    <NumberInput
                      label=" "
                      placeholder="請輸入數值"
                      min={0}
                      max={100}
                      decimalScale={2}
                      key={form.key(
                        `profitSharingConfigs.${parkingLotFunctionType}.defaultConfig.value`,
                      )}
                      {...form.getInputProps(
                        `profitSharingConfigs.${parkingLotFunctionType}.defaultConfig.value`,
                      )}
                    ></NumberInput>
                  </Grid.Col>
                </Grid>

                <Grid columns={4}>
                  <Grid.Col span={3}>
                    <Select
                      withAsterisk
                      allowDeselect={false}
                      label="有銀行折抵時分潤條件"
                      data={bankDiscountProfitSharingTypeSelections}
                      style={{ flex: 1 }}
                      onClick={() => {
                        if (
                          form.values.profitSharingConfigs[
                            parkingLotFunctionType
                          ].bankDiscountConfig.type !== 'NON'
                        ) {
                          form.setFieldValue(
                            `profitSharingConfigs.${parkingLotFunctionType}.bankDiscountConfig.value`,
                            null,
                          );
                        }
                      }}
                      key={form.key(
                        `profitSharingConfigs.${parkingLotFunctionType}.bankDiscountConfig.type`,
                      )}
                      {...form.getInputProps(
                        `profitSharingConfigs.${parkingLotFunctionType}.bankDiscountConfig.type`,
                      )}
                    />
                  </Grid.Col>
                  {form.values.profitSharingConfigs[parkingLotFunctionType]
                    .bankDiscountConfig.type !== 'NON' && (
                    <Grid.Col span={1}>
                      <NumberInput
                        label=" "
                        placeholder="請輸入數值"
                        min={0}
                        max={100}
                        decimalScale={2}
                        key={form.key(
                          `profitSharingConfigs.${parkingLotFunctionType}.bankDiscountConfig.value`,
                        )}
                        {...form.getInputProps(
                          `profitSharingConfigs.${parkingLotFunctionType}.bankDiscountConfig.value`,
                        )}
                      ></NumberInput>
                    </Grid.Col>
                  )}
                </Grid>

                <Grid columns={4}>
                  <Grid.Col span={3}>
                    <Select
                      withAsterisk
                      allowDeselect={false}
                      label="有 PBA 導流時分潤條件"
                      data={pbaReferralProfitSharingTypeSelections}
                      style={{ flex: 1 }}
                      onClick={() => {
                        if (
                          form.values.profitSharingConfigs[
                            parkingLotFunctionType
                          ].pbaReferralConfig.type !== 'NON'
                        ) {
                          form.setFieldValue(
                            `profitSharingConfigs.${parkingLotFunctionType}.pbaReferralConfig.value`,
                            null,
                          );
                        }
                      }}
                      key={form.key(
                        `profitSharingConfigs.${parkingLotFunctionType}.pbaReferralConfig.type`,
                      )}
                      {...form.getInputProps(
                        `profitSharingConfigs.${parkingLotFunctionType}.pbaReferralConfig.type`,
                      )}
                    />
                  </Grid.Col>
                  {form.values.profitSharingConfigs[parkingLotFunctionType]
                    .pbaReferralConfig.type !== 'NON' && (
                    <Grid.Col span={1}>
                      <NumberInput
                        label=" "
                        placeholder="請輸入數值"
                        min={0}
                        max={100}
                        decimalScale={2}
                        key={form.key(
                          `profitSharingConfigs.${parkingLotFunctionType}.pbaReferralConfig.value`,
                        )}
                        {...form.getInputProps(
                          `profitSharingConfigs.${parkingLotFunctionType}.pbaReferralConfig.value`,
                        )}
                      ></NumberInput>
                    </Grid.Col>
                  )}
                </Grid>

                <Radio.Group
                  name={`profitSharingConfigs.${parkingLotFunctionType}.allowDisbursementAmountOverLimit`}
                  label="是否允許撥款金額超過原始請款金額"
                  key={form.key(
                    `profitSharingConfigs.${parkingLotFunctionType}.allowDisbursementAmountOverLimit`,
                  )}
                  value={
                    form.values.profitSharingConfigs[parkingLotFunctionType]
                      .allowDisbursementAmountOverLimit
                      ? 'Y'
                      : 'N'
                  }
                  onChange={(v) =>
                    form.setFieldValue(
                      `profitSharingConfigs.${parkingLotFunctionType}.allowDisbursementAmountOverLimit`,
                      v === 'Y',
                    )
                  }
                >
                  <Group mt={20}>
                    <MantineProvider theme={{ cursorType: 'pointer' }}>
                      <Radio value="Y" label="是" />
                      <Radio value="N" label="否" />
                    </MantineProvider>
                  </Group>
                </Radio.Group>
              </SimpleGrid>
            </>
          );
        },
      )}

      <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
        合約上傳
      </Text>
      {displayAttachments.length === 0 && (
        <>
          <Dropzone
            onDrop={(files) => onDrop(files)}
            onReject={() => alert('只可上傳 < 30MB 的 PDF 檔案！')}
            maxSize={5 * 1024 ** 2}
            accept={PDF_MIME_TYPE}
            pt={0}
            {...dropZoneProps}
            style={{
              border: form.errors.newAttachments
                ? '2px dashed var(--mantine-color-red-6 )'
                : '2px dashed var(--mantine-color-gray-2 )',
            }}
          >
            <Group
              justify="center"
              gap="xl"
              mih={220}
              style={{ cursor: 'pointer' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size={52}
                  color={theme.colors.blue[6]}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} color={theme.colors.red[6]} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconCloudUpload size={52} color="#212529" stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  上傳停車場紙本合約
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  把檔案拖曳到這裡上傳。只接受小於 30MB 的 .pdf 檔案。
                </Text>
              </div>
            </Group>
          </Dropzone>
        </>
      )}
      {displayAttachments.length > 0 && (
        <>
          <Text size="sm" fw={500} inline mb={10}>
            已選擇檔案：
          </Text>
          {displayAttachments.map((attachment) => {
            return (
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  {attachment.displayName}
                </Text>
                <Button
                  variant="transparent"
                  color="red"
                  onClick={() => onClickDeleteFile(attachment)}
                >
                  <Text size="sm" fw={500} td="underline">
                    刪除檔案
                  </Text>
                </Button>
              </Group>
            );
          })}
        </>
      )}

      {form.errors.newAttachments && (
        <Text c="red" fz="xs" mt={4}>
          {form.errors.newAttachments}
        </Text>
      )}
    </form>
  );
}
